import { useContext } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { TGroup } from 'types';

import {
  ICreateCategoryArgs,
  IUpdateCategoryArgs,
  TCreateCategory,
  TDeleteCategory,
  TUpdateCategory,
} from './interfaces';
import {
  CATEGORIES,
  CREATE_CATEGORY,
  DELETE_CATEGORY,
  UPDATE_CATEGORY,
} from './schema';

export const useCategoryRequests = ({
  setIsDeletingError,
}: {
  setIsDeletingError?: (value: boolean) => void;
}): {
  updateCategory: (args: IUpdateCategoryArgs) => Promise<unknown>;
  isCategoryLoading: boolean;
  loadedCategories: TGroup[];
  createCategory: (args: ICreateCategoryArgs) => Promise<unknown>;
  deleteCategory: (id: number) => Promise<unknown>;
  toggleShowInShellCategory: (args: IUpdateCategoryArgs) => Promise<unknown>;
} => {
  const { t } = useContext(LocalizationContext);
  const { data, loading } = useQuery<{
    categories: Array<TGroup>;
  }>(CATEGORIES, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [createCategoryRequest] = useMutation<{
    createCategory: { id: number };
  }>(CREATE_CATEGORY, {
    fetchPolicy: 'no-cache',
    refetchQueries: [CATEGORIES],
    onCompleted: () => {
      notification.info({
        t,
        type: 'success',
        title: t('group.created'),
        dismiss: 5,
      });
    },
  });

  const createCategory: TCreateCategory = ({
    title,
    show_in_shell,
    sell_periods,
  }) =>
    createCategoryRequest({
      variables: {
        input: {
          title,
          show_in_shell,
          sell_periods,
        },
      },
    }).then();

  const [updateCategoryRequest] = useMutation(UPDATE_CATEGORY, {
    fetchPolicy: 'no-cache',
    refetchQueries: [CATEGORIES],
    onCompleted: () =>
      notification.info({
        t,
        type: 'success',
        title: t('group.updated'),
        dismiss: 5,
      }),
  });
  const updateCategory: TUpdateCategory = ({
    id,
    title,
    show_in_shell,
    sell_periods,
  }) =>
    updateCategoryRequest({
      variables: {
        id,
        input: {
          title,
          show_in_shell,
          sell_periods,
        },
      },
    });

  const [toggleShowInShellCategoryRequest] = useMutation(UPDATE_CATEGORY, {
    fetchPolicy: 'no-cache',
    refetchQueries: [CATEGORIES],
    onCompleted: (data) => {
      const showInShell = data?.updateCategory?.show_in_shell;
      const title = data?.updateCategory?.title;

      notification.info({
        t,
        type: 'success',
        title: t('group.table.show_in_shell.title'),
        message: showInShell
          ? `«${title}» ${t('goods.update.show_in_shell.yes')}`
          : `«${title}» ${t('goods.update.show_in_shell.no')}`,
        dismiss: 5,
      });
    },
  });

  const toggleShowInShellCategory: TUpdateCategory = async ({
    id,
    title,
    show_in_shell,
  }): Promise<void> => {
    try {
      await toggleShowInShellCategoryRequest({
        variables: {
          id,
          input: {
            title,
            show_in_shell: !show_in_shell,
          },
        },
      });
    } catch (error) {
      console.error('Ошибка при обновлении товара:', error);
    }
  };

  const [deleteCategoryRequest] = useMutation(DELETE_CATEGORY, {
    fetchPolicy: 'no-cache',
    refetchQueries: [CATEGORIES],
    onCompleted: ({ deleteCategory }) =>
      notification.info({
        t,
        type: 'success',
        title: t('group.deleted'),
        message: deleteCategory.title,
        dismiss: 5,
      }),
    onError: () => {
      // Знаю надо прописать кейсы но не знаю где взять кейс ошибок
      setIsDeletingError?.(true);
      const errorMessage = t('group.delete.error.message');

      return notification.info({
        t,
        type: 'error',
        title: t('group.delete.error.title'),
        message: errorMessage,
        dismiss: 5,
      });
    },
  });

  const deleteCategory: TDeleteCategory = (id) => {
    return deleteCategoryRequest({ variables: { id } });
  };

  return {
    isCategoryLoading: loading,
    loadedCategories: data?.categories ?? [],
    createCategory,
    deleteCategory,
    updateCategory,
    toggleShowInShellCategory,
  };
};
